import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { ArrowLeft } from 'lucide-react'

interface DashboardShellProps {
  title: string
  subtitle: string
  headerAction?: ReactNode
  backTo?: { label: string; path: string }
  children: ReactNode
}

export default function DashboardShell({
  title,
  subtitle,
  headerAction,
  backTo,
  children,
}: DashboardShellProps) {
  return (
    <div
      className="flex flex-col items-center"
      style={{ minHeight: '100vh', paddingBottom: '4rem' }}
    >
      {/* Ambient radial glow */}
      <div
        className="fixed pointer-events-none z-0"
        style={{
          top: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 600,
          height: 400,
          background: 'radial-gradient(ellipse, rgba(6,182,212,0.04) 0%, transparent 70%)',
        }}
      />

      <div className="w-full max-w-4xl mx-auto px-4 pt-8 relative z-10">
        {/* Back link */}
        {backTo && (
          <Link
            to={backTo.path}
            className="inline-flex items-center gap-2 font-mono text-sm text-cyan-400/70 hover:text-cyan-400 transition-colors duration-150 mb-5"
          >
            <ArrowLeft className="w-4 h-4" />
            {backTo.label}
          </Link>
        )}

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-mono text-sm text-cyan-500/60 tracking-[0.3em] uppercase mb-1">
              {title}
            </h1>
            <p className="font-mono text-base text-slate-500">{subtitle}</p>
          </div>
          {headerAction}
        </div>

        {/* Content with transition */}
        <AnimatePresence mode="wait">
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.15 }}
          >
            {children}
          </motion.div>
        </AnimatePresence>

      </div>
    </div>
  )
}
